import React from 'react';
import classNames from 'classnames';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';

import styles from './Pagination.module.scss';

/**
 * Simple bordered-square pager: prev arrow, page numbers, next arrow.
 * Optionally paired with a rows-per-page select — pass `pageSize` +
 * `pageSizeOptions` + `onPageSizeChange` to opt in (used by Tasks); callers
 * that omit them get the exact same output as before this option existed.
 * Renders nothing when there's only one page and no page-size control —
 * pagination controls should only appear once there's something to do.
 */
export const Pagination = ({
  currentPage,
  totalPages,
  onPageChange,
  pageSize,
  pageSizeOptions,
  onPageSizeChange,
}) => {
  const showPageSize = Boolean(pageSizeOptions?.length && onPageSizeChange);
  const showPager = totalPages > 1;
  if (!showPager && !showPageSize) return null;

  const pages = showPager
    ? Array.from({ length: totalPages }, (_, i) => i + 1)
    : [];

  return (
    <div className={styles.paginationBar}>
      {showPageSize && (
        <select
          className={styles.pageSizeSelect}
          value={pageSize}
          onChange={(e) => onPageSizeChange(Number(e.target.value))}
          aria-label="Rows per page"
        >
          {pageSizeOptions.map((size) => (
            <option key={size} value={size}>
              {size} per page
            </option>
          ))}
        </select>
      )}
      {showPager && (
        <div className={styles.pagination}>
          <button
            type="button"
            className={styles.navButton}
            disabled={currentPage <= 1}
            onClick={() => onPageChange(currentPage - 1)}
            aria-label="Previous page"
          >
            <FiChevronLeft size={16} />
          </button>
          {pages.map((page) => (
            <button
              key={page}
              type="button"
              className={classNames(styles.pageButton, {
                [styles.pageButton_active]: page === currentPage,
              })}
              onClick={() => onPageChange(page)}
            >
              {page}
            </button>
          ))}
          <button
            type="button"
            className={styles.navButton}
            disabled={currentPage >= totalPages}
            onClick={() => onPageChange(currentPage + 1)}
            aria-label="Next page"
          >
            <FiChevronRight size={16} />
          </button>
        </div>
      )}
    </div>
  );
};

Pagination.displayName = 'Pagination';
